// theme.js
// Central MUI theme, and the colour tokens the app shares (BRAND, DATA), so
// components don't hand-roll colours.

import { createTheme } from "@mui/material/styles";

// The palette: the grid's colour ramp (rarest to most common), the accent and
// the background's glows. Everything with colour takes it from here, so the
// grid, the logo (built from the ramp's stops), the background and the buttons
// stay one family: teal, cyan, indigo, magenta and pink, with the ramp's
// light blue (cyan) as the accent.
export const PALETTE = {
  ramp: [[0, [18, 52, 86]], [0.18, [57, 208, 255]], [0.45, [111, 93, 245]], [0.72, [200, 59, 212]], [1, [255, 77, 166]]],
  accent: [57, 208, 255],
  accentHover: "#80e1ff",
  onAccent: "#06131a",
  glows: ["rgba(111,93,245,0.22)", "rgba(57,208,255,0.14)", "rgba(200,59,212,0.10)"],
};

const hex = (c) => `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
const STOPS = PALETTE.ramp.map(([, c]) => hex(c)); // rarest to most common

// The accent at any opacity, for hover and selected backgrounds and glows.
export const accentTint = (alpha) => `rgba(${PALETTE.accent.join(",")},${alpha})`;

export const BRAND = {
  bg: "#0f0f13",
  surface: "rgba(25, 25, 30, 0.9)",
  surfaceStrong: "rgba(25, 25, 30, 0.95)",
  // The accent marks what you can tap: links, buttons, Play, the current
  // page, focus rings.
  accent: hex(PALETTE.accent),
  accentHover: PALETTE.accentHover,
  accentSoft: accentTint(0.1), // hover and selected backgrounds
  accentLine: accentTint(0.45), // outlines
  onAccent: PALETTE.onAccent, // text on an accent fill
  // Highlighted figures, badges and rings on grid squares (a scorigami, a
  // champion, a solved puzzle): the accent too, so the site has one brand
  // colour beside the grid's. Rings sit 2px off their square, so they show on
  // squares of any colour.
  highlight: hex(PALETTE.accent),
  highlightSoft: accentTint(0.12),
  highlightLine: accentTint(0.45),
  textSecondary: "#b0b0b0",
  divider: "rgba(255,255,255,0.12)",
};

// The ramp's stops, for charts and the head-to-head split, so data looks like
// the grid; never for buttons. `deep` sits between the two rarest stops.
const mix = (a, b, k) => a.map((v, i) => Math.round(v + (b[i] - v) * k));
export const DATA = { deep: hex(mix(PALETTE.ramp[0][1], PALETTE.ramp[1][1], 0.35)), rare: STOPS[1], mid: STOPS[2], warm: STOPS[3], hot: STOPS[4] };

// A ramp stop (0 rarest to 4 most common) at any opacity.
export const stopTint = (i, alpha) => `rgba(${PALETTE.ramp[i][1].join(",")},${alpha})`;

// Near-black or white, whichever reads better on a colour ("#rrggbb").
export const textOn = (color) => {
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const [r, g, b] = [1, 3, 5].map((i) => lin(parseInt(color.slice(i, i + 2), 16) / 255));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.18 ? "rgba(10,10,14,0.9)" : "#fff";
};

// The logo: a 3×3 slice of the grid, the most common colour top left and the
// rarest bottom right (each diagonal is one stop of the ramp).
export const LOGO_CELLS = [0, 1, 2].map((r) => [0, 1, 2].map((c) => STOPS[4 - r - c]));

// The background's three soft glows.
export const GLOWS = PALETTE.glows;

// A filled accent button: the main action on a page.
export const accentButtonSx = {
  background: BRAND.accent,
  color: BRAND.onAccent,
  "&:hover": { background: BRAND.accentHover },
};

const theme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: BRAND.bg,
      paper: BRAND.surface,
    },
    primary: { main: BRAND.accent, contrastText: BRAND.onAccent },
    secondary: { main: BRAND.highlight, contrastText: BRAND.onAccent },
    text: { primary: "#fff", secondary: BRAND.textSecondary },
  },
  components: {
    // Evaluate media queries on the first render (no desktop-then-mobile
    // flip), so the first client render matches a pre-rendered page.
    MuiUseMediaQuery: { defaultProps: { noSsr: true } },
  },
  typography: {
    fontFamily: '"Poppins","Helvetica","Arial",sans-serif',
    h3: { fontWeight: 700, color: "#fff" },
  },
});

export default theme;
